import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeMode, ThemeState } from '../type';

const STORAGE_KEY = 'rf-theme';

/**
 * Applies the mode to `<html>`, which is what the `ds-*` token layer keys off:
 * `tailwind.config.js` emits the dark palette under `:root.dark`.
 *
 * Done as a side effect of the store rather than in a `useEffect` somewhere,
 * because the class has to survive a page load before React mounts - otherwise
 * dark-mode users get a white flash on every navigation.
 */
const applyMode = (mode: ThemeMode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', mode === 'dark');
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'light',
      setMode: (mode) => {
        applyMode(mode);
        set({ mode });
      },
      toggleMode: () => {
        const next: ThemeMode = get().mode === 'dark' ? 'light' : 'dark';
        applyMode(next);
        set({ mode: next });
      },
    }),
    {
      name: STORAGE_KEY,
      // Re-apply on rehydrate: `persist` restores the value after the store is
      // created, so the class set at creation time would otherwise be stale.
      onRehydrateStorage: () => (state) => {
        if (state) applyMode(state.mode);
      },
    }
  )
);
